import React from 'react';
import T from 'i18n';
import {
  faPlusCircle,
  faTrashAlt,
  faEdit,
  faDownload,
  faExchangeAlt,
  faLink,
  faInfoCircle,
  faClone,
  faPaste,
  faEye,
  faFileUpload,
  faIndent,
  faCut,
  faMinusCircle,
  faArrowCircleUp,
  faPencilAlt,
  faMap,
  faSync,
  faList,
} from '@fortawesome/free-solid-svg-icons';
import { faFolderOpen } from '@fortawesome/free-regular-svg-icons';
import { faChromecast } from '@fortawesome/free-brands-svg-icons';
import { documentModalActions } from '../../enums';
import history from 'common/history';
import CircularProgress from '@mui/material/CircularProgress';

export const buttonActions = ({
  selectedRows,
  isPresenterTools,
  permissions = {},
  downloadedDocuments,
  currentFolder,
  openDialog,
  handleShow,
  showGenerateHyperlinks,
  showExhibitNumber,
  enableRemovePages = false,
  canUpdateExhibitReferences,
  updateReferencesDisabled = false,
  isAllDocuments,
  getFolderSpecificFileDetails,
  openFileLoading,
  isConfidentialityRing,
  openDialogCR,
  sortMode,
  handleSidebar,
  fileCount,
  globalBatchDrawerOpen,
  showSidebar,
  setEditMode,
  editMode,
  openMapDataDrawer,
  inlineEdit,
  isDocFinderPage,
  showPresentModeWithSearch,
  onOpenViewDrawer,
  openCreatePlaceholdersModal,
  showIManageTools,
  userId,
  caseId,
  openIManageDrawer,
}: any) => {
  const {
    folders: {
      canUploadDocuments = undefined,
      canRemoveDocuments = undefined,
      canDownloadDocuments = undefined,
      canCreatePlaceholders = undefined,
    } = {},
    documents: {
      canUpdateAnyData = undefined,
      canReplace = undefined,
      canSplit = undefined,
      canRemovePages = undefined,
      canGeneratePublicHyperlinks = undefined,
      canSeeFileVersions = undefined,
      canAssignDocsToRings = undefined,
      canCreateAnnotations = undefined,
      canUploadTranscriptNotes = undefined,
    } = {},
    hearingRoom: { canPresent = undefined } = {},
  } = permissions;

  const canDownload = canDownloadDocuments;
  const canUpload = canUploadDocuments && canUploadDocuments();
  const canCreatePlaceholdersPermission =
    canCreatePlaceholders && canCreatePlaceholders();
  const canRemove = canRemoveDocuments && canRemoveDocuments();
  const canUpdateData =
    canUpdateAnyData && selectedRows.every((file: any) => canUpdateAnyData(file));
  const canGeneratePublicHyperlinksFile =
    !isPresenterTools && canGeneratePublicHyperlinks && canGeneratePublicHyperlinks();
  const canShowFileVersions = canSeeFileVersions && canSeeFileVersions(selectedRows[0]);
  const assignDocsToRing = canAssignDocsToRings && canAssignDocsToRings(selectedRows[0]);
  const canReplaceFile = canReplace && canReplace(selectedRows[0]);
  const canSplitFile = canSplit && canSplit(selectedRows[0]);
  const isMergeSourceFile = selectedRows[0]?.source === 'merge';
  const canRemovePagesFile =
    !isMergeSourceFile &&
    enableRemovePages &&
    ((canRemovePages && canRemovePages(selectedRows[0])) ?? canSplitFile);
  const canDuplicate = permissions?.documents?.duplicate === true;
  const isAiNotReady = selectedRows[0]?.aiNotReady;
  const selectedFileType = (selectedRows[0]?.type || '').toLowerCase();
  const isDocxFile = selectedFileType === 'docx';
  const anySelectedDownloading = selectedRows.some((row: any) => {
    const match = downloadedDocuments.find(({ fileId }: any) => fileId === row.id);
    return match && (match.progress || match.printProgress || match.originalProgress);
  });
  const isMacPlatform =
    typeof navigator !== 'undefined' &&
    typeof navigator.platform === 'string' &&
    navigator.platform.toLowerCase().includes('mac');
  const updateModalShortcutText = T.translate('common.openUpdateModalShrtcut', {
    shortcut: isMacPlatform ? 'Cmd+Ctrl+M' : 'Ctrl+M',
  });
  const presentViewShortcutText = T.translate('common.presentViewShortcut', {
    shortcut: isMacPlatform ? 'Cmd+Shift+P' : 'Ctrl+Shift+P',
  });
  const withLoader = (label: React.ReactNode) =>
    openFileLoading ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <CircularProgress size={12} thickness={5} sx={{ color: '#fff' }} />
        <span>{label}</span>
      </span>
    ) : (
      label
    );

  const batchUploadEnabled = permissions.documents.batchUpload;
  // check if the selectRows Array contains a file that is "agreed"
  const hasAgreed = selectedRows.some((file: any) => file.agreed);
  const isAgreedBundle = currentFolder?.agreed;

  const changePositionDisabled = hasAgreed || sortMode === false || selectedRows.length < 1;
  const isLeafFolder =
    !currentFolder || !Array.isArray(currentFolder.folders) || currentFolder.folders.length === 0;
  const isSupportedPlaceholderBundle =
    currentFolder &&
    ['trialbook', 'team-bundle', 'private-bundle', 'bundle', 'court-bundle'].includes(
      currentFolder.type,
    );

  return [
    canUpload &&
      !isAllDocuments &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly && {
        title: isAgreedBundle ? T.translate('case.lateInsert') : T.translate('generic.uploadFile'),
        handler: openDialog,
        icon: isAgreedBundle ? faFileUpload : faPlusCircle,
        disabled: showSidebar,
      },
    isConfidentialityRing &&
      canUpload &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly && {
        title: T.translate('generic.uploadFileInner'),
        handler: openDialogCR,
        icon: faPlusCircle,
      },
    openCreatePlaceholdersModal &&
      canCreatePlaceholdersPermission &&
      isSupportedPlaceholderBundle &&
      !isAllDocuments &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly &&
      isLeafFolder && {
        title: T.translate('case.createPlaceholders') || 'Create placeholders',
        handler: openCreatePlaceholdersModal,
        icon: faPlusCircle,
        disabled: showSidebar,
      },
    canUpload &&
      !isAllDocuments &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly && { divider: true },
    canDownload && {
      id: 'download',
      title: T.translate('generic.download'),
      handler: () => {
        handleShow(documentModalActions.download);
      },
      icon: faDownload,
      disabled: selectedRows.length < 1 || anySelectedDownloading,
    },
    batchUploadEnabled && { divider: true },

    batchUploadEnabled &&
      !isAllDocuments &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly &&
      !globalBatchDrawerOpen && {
        title: T.translate('generic.batchUpload'),
        handler: handleSidebar,
        icon: faArrowCircleUp,
        disabled: !batchUploadEnabled || isAgreedBundle,
      },

    batchUploadEnabled && // use same permission as upload
      !isAllDocuments &&
      currentFolder && {
        title: T.translate('generic.mapData'),
        handler: openMapDataDrawer,
        icon: faMap,
        disabled: fileCount < 1,
      },

    showIManageTools &&
      ['team-bundle', 'private-bundle'].includes(currentFolder?.type) && { divider: true },
    showIManageTools &&
      !isAllDocuments &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly &&
      ['team-bundle', 'private-bundle'].includes(currentFolder.type) && {
        title: T.translate('case.iManageImport'),
        handler: () => {
          const segmentByType: Record<string, string> = {
            bundle: 'bundles',
            'team-bundle': 'teamBundles',
            'private-bundle': 'privateBundles',
            trialbook: 'trialbundles',
            'court-bundle': 'court-bundles',
            'group-bundle': 'group-bundles',
          };
          const segment = segmentByType[currentFolder?.type];
          const rootFolderLink =
            segment && currentFolder?.code
              ? `/case/${caseId}/${segment}/${currentFolder.code}`
              : '';
          const iManageBaseUrl =
            process.env.IMANAGE_WEB_APP_URL || 'https://dev.imanage.trialviewlive.com';
          window.open(
            `${iManageBaseUrl}/FilesUploadDialog/${userId}?caseId=${caseId}&folderId=${currentFolder?.id || ''}&rootFolderLink=${encodeURIComponent(rootFolderLink)}`,
            '_blank',
            'noopener,noreferrer',
          );
          if (openIManageDrawer) openIManageDrawer();
        },
        icon: faExchangeAlt,
      },

    (canUpload || canDownload) && { divider: true },

    {
      title: T.translate('generic.copyTo'),
      handler: () => handleShow(documentModalActions.copy),
      icon: faFolderOpen,
      disabled: selectedRows.length < 1 || isDocFinderPage,
    },
    canRemove &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        title: T.translate('generic.removeFrom'),
        handler: () => handleShow(documentModalActions.remove),
        icon: faTrashAlt,
        disabled: selectedRows.length < 1,
      },
    canDuplicate &&
      !isPresenterTools &&
      currentFolder &&
      currentFolder.id &&
      !currentFolder.readOnly && {
        title: T.translate('generic.duplicate'),
        handler: () => handleShow(documentModalActions.duplicate),
        icon: faClone,
        disabled: selectedRows.length !== 1,
      },

    (canRemove || canUpload) && canUpdateData && { divider: true },
    inlineEdit && {
      title: editMode ? T.translate('generic.stopEditing') : T.translate('generic.editNames'),
      icon: faPencilAlt,
      handler: () => setEditMode((prev: boolean) => !prev),
    },
    canUpdateData &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        id: 'updateMeta',
        title: (
          <span style={{ display: 'inline-flex', width: '100%' }}>
            {T.translate('generic.updateMeta')}
            <span className="shortcutsText">{updateModalShortcutText}</span>
          </span>
        ),
        subTitle: T.translate('generic.updateMeta'),
        handler: () => handleShow(documentModalActions.updateMeta),
        icon: faEdit,
        disabled: selectedRows.length !== 1,
      },
    canUpdateData &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        id: 'changePosition',
        title: `${T.translate('generic.changePosition')}`,
        handler: () => handleShow(documentModalActions.changePosition),
        icon: faIndent,
        disabled: changePositionDisabled,
      },
    canReplaceFile &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        title: T.translate('generic.replaceFile'),
        handler: () => handleShow(documentModalActions.replace),
        icon: faExchangeAlt,
        disabled: selectedRows.length !== 1,
      },
    canSplitFile &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        title: T.translate('generic.splitFile'),
        handler: () => handleShow(documentModalActions.split),
        icon: faCut,
        disabled: selectedRows.length !== 1,
        tooltip: isAiNotReady ? T.translate('generic.aiNotReady') : undefined,
      },
    canRemovePagesFile &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        title: T.translate('generic.removePages'),
        handler: () => handleShow(documentModalActions.removePages),
        icon: faMinusCircle,
        disabled: selectedRows.length !== 1,
      },
    showExhibitNumber &&
      canUpdateExhibitReferences &&
      isDocxFile && {
        title: T.translate('generic.updateReferences'),
        handler: () => handleShow(documentModalActions.updateReferences),
        icon: faSync,
        disabled: selectedRows.length !== 1,
      },
    showExhibitNumber &&
      isDocxFile && {
        title: T.translate('generic.exhibitChanges'),
        handler: () => handleShow(documentModalActions.exhibitChanges),
        icon: faList,
        disabled: selectedRows.length !== 1,
      },
    // {
    //   id: 'view',
    //   title: <span>{T.translate('generic.view')}</span>,
    //   subTitle: withLoader(T.translate('generic.view')),
    //   handler: () => getFolderSpecificFileDetails(selectedRows, 'normal'),
    //   icon: faEye,
    //   disabled: selectedRows.length !== 1 || openFileLoading,
    // },
    onOpenViewDrawer && {
      id: 'focusView',
      title: <span>{T.translate('generic.focusView')}</span>,
      subTitle: T.translate('generic.focusView'),
      handler: () => onOpenViewDrawer(selectedRows[0]),
      icon: faEye,
      disabled: selectedRows.length !== 1,
    },
    (canUpdateData || canReplaceFile) &&
      canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && { divider: true },
    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && {
        header: T.translate('generic.generateHyperlinksFrom'),
        title: T.translate('generic.findAndReplaceHyperlinks'),
        handler: () => handleShow(documentModalActions.findAndReplaceHyperlinks),
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },

    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && {
        title: T.translate('generic.generateHyperlinksPreview'),
        handler: () => handleShow(documentModalActions.previewHyperlinks), // from external id
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },
    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && {
        title: T.translate('generic.generateHyperlinksPreviewWithDiscoveryId'),
        handler: () => handleShow(documentModalActions.previewHyperlinksWithDiscoveryID), // from discovery id
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },
    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      showExhibitNumber &&
      !isAllDocuments && {
        title: T.translate('generic.generateFromExhibitNumber'),
        handler: () => handleShow(documentModalActions.exhibitNumberHyperlinks), // from exhibit numbers
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },
    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && {
        header: T.translate('generic.manageHyperlinksHeader'),
        title: T.translate('generic.manageHyperlinks'),
        handler: () => handleShow(documentModalActions.manageHyperlinks),
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },

    canGeneratePublicHyperlinks && showGenerateHyperlinks && !isAllDocuments && { divider: true },
    canPresent &&
      canPresent('public') && {
        id: 'presentToHearingRoom',
        title: (
          <span style={{ display: 'inline-flex', width: '100%' }}>
            {T.translate('generic.present')}
            <span className="shortcutsText">
              {T.translate('common.presentToHearingRoomShortcut')}
            </span>
          </span>
        ),
        subTitle: T.translate('generic.present'),
        handler: () => {
          history.push(
            history.location.pathname.replace(/\/files.*/, ``) +
              `/files/${selectedRows[0].id}?present=public`,
          );
        },
        icon: faChromecast,
        disabled: selectedRows.length !== 1 || selectedRows[0].private,
      },

    canPresent &&
      showPresentModeWithSearch &&
      canPresent('public') && {
        id: 'presentView',
        title: (
          <span style={{ display: 'inline-flex', width: '100%' }}>
            {T.translate('case.presentViewTitle')}
            <span className="shortcutsText">{presentViewShortcutText}</span>
          </span>
        ),
        subTitle: withLoader(T.translate('case.presentViewTitle')),
        handler: () => getFolderSpecificFileDetails(selectedRows, 'share'),
        icon: faEye,
        disabled:
          selectedRows.length !== 1 || (!canPresent && !canPresent('public')) || openFileLoading,
      },
    canCreateAnnotations && { divider: true },
    canUploadTranscriptNotes && {
      id: 'transcriptNotes',
      title: T.translate('generic.transcriptNotes'),
      handler: () => handleShow(documentModalActions.previewTranscriptNotes),
      icon: faFileUpload,
      disabled: selectedRows.length !== 1,
    },
    { divider: true },
    canShowFileVersions && {
      title: T.translate('generic.fileVersions'),
      handler: () => handleShow(documentModalActions.fileVersions),
      icon: faClone,
      disabled: selectedRows.length !== 1,
    },
    isConfidentialityRing &&
      assignDocsToRing && {
        title: T.translate('generic.fileVariants'),
        handler: () => handleShow(documentModalActions.fileVariants),
        icon: faPaste,
        disabled: selectedRows.length !== 1,
      },
    {
      title: T.translate('generic.more'),
      handler: () => handleShow(documentModalActions.moreInfo),
      icon: faInfoCircle,
      disabled: selectedRows.length !== 1,
    },
  ];
};
